<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { DialogVariants } from '.';
import { provide, shallowRef } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { X } from 'lucide-vue-next';
import { DialogClose, DialogContent, DialogOverlay, DialogPortal, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';
import { dialogVariants } from '.';

interface Props extends DialogContentProps {
    class?: HTMLAttributes['class'];
    size?: DialogVariants['size'];
    /** Hide the built-in top-right close button (e.g. destructive confirmations
     * that must be answered with an explicit choice). */
    hideCloseButton?: boolean;
}

defineOptions({
    inheritAttrs: false,
});

const props = defineProps<Props>();

const emits = defineEmits<DialogContentEmits>();

const delegatedProps = reactiveOmit(props, 'class', 'size', 'hideCloseButton');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

// Popovers/tooltips opened inside this dialog must portal to <body>, not into
// App.vue's PortalContainer: that div sits inside #app, BEFORE this dialog, and
// reka ranks nested layers by DOM order — so picking a select option read as a
// click outside the dialog and closed it (ITDEVELOP-3523). An empty container
// makes PopoverContent/TooltipContent fall back to 'body', after the dialog.
provide('portalContainerRef', shallowRef(null));

// PrimeVue's DatePicker panel is appended to <body> and is not a reka layer, so
// inside this dialog it was dead (ITDEVELOP-3523): the modal sets
// pointer-events:none on <body>, which the panel inherits (re-enabled in the
// <style> below), and a click on it then reads as outside the dialog.
const PRIMEVUE_OVERLAY = '.p-datepicker-panel';

function keepPrimeVueOverlaysInside(event: CustomEvent<{ originalEvent: Event }>) {
    const target = event.detail.originalEvent.target;
    if (target instanceof Element && target.closest(PRIMEVUE_OVERLAY)) event.preventDefault();
}
</script>

<template>
    <DialogPortal>
        <!-- Scrim and surface are themed via the overlay role tokens, so the
             dialog is dark-ready with no dark: variants: --overlay-scrim and
             --overlay-surface flip under .dark (see default.css). -->
        <DialogOverlay
            class="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-60 bg-[var(--overlay-scrim)]"
        />
        <DialogContent
            :class="
                cn(
                    dialogVariants({ size }),
                    props.class,
                )
            "
            v-bind="{ ...forwarded, ...$attrs }"
            @interact-outside="keepPrimeVueOverlaysInside"
        >
            <slot />

            <DialogClose
                v-if="!hideCloseButton"
                class="absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)] disabled:pointer-events-none"
                aria-label="Close"
            >
                <!-- size-6: every purpose-built overlay close (filter sheets,
                     incident form) is 24px; the 16px shadcn default was the
                     odd one out (MR !312 review). -->
                <X class="h-6 w-6" />
            </DialogClose>
        </DialogContent>
    </DialogPortal>
</template>

<style>
/* Global on purpose: the panel is a PrimeVue element outside this component.
   A no-op unless a modal has disabled pointer events on <body>. */
.p-datepicker-panel {
    pointer-events: auto;
}
</style>
